import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useNavigate } from "react-router-dom";
import {
  IconButton,
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PeopleIcon from "@mui/icons-material/People";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TireRepair from "@mui/icons-material/TireRepair";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DateRangeIcon from "@mui/icons-material/DateRange";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssessmentIcon from "@mui/icons-material/Assessment";
import OilBarrel from "@mui/icons-material/OilBarrel";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CarRepair from "@mui/icons-material/CarRepair";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import { isIncludedGroupServicePart } from "../utils/receiptCalculations";

function WeeklyReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicle] = useState([]);
  const [loading, setLoading] = useState(true);

  const isInvoiceReceipt = (receipt) =>
    String(receipt?.receiptType || "").toLowerCase() === "invoice";

  const parseLocalDateInput = (value) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };

  const toLocalDateInput = (value = new Date()) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentWeekRange = () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - today.getUTCDay());
    const end = new Date(today);
    end.setDate(today.getDate() - today.getUTCDay() + 6);
    return {
      start: toLocalDateInput(start),
      end: toLocalDateInput(end),
    };
  };

  const initialWeek = getCurrentWeekRange();
  const [startDate, setStartDate] = useState(initialWeek.start);
  const [endDate, setEndDate] = useState(initialWeek.end);

  const calcPrice = (item) => {
    let total = 0;
    for (let index = 0; index < item.length; index++) {
      total += item[index].totalPrice;
    }
    return total.toFixed(2);
  };

  const formatCurrency = (value) => Number(value || 0).toFixed(2);

  const renderSectionTitle = (title, icon, color) => (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          backgroundColor: `${color}14`,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Stack>
  );

  const weeklyBreakdown = useMemo(() => {
    const tireMap = new Map();
    const oilMap = new Map();
    const inspectionMap = new Map();
    const serviceMap = new Map();

    const addToMap = (map, label, count, amount) => {
      const description = String(label || "").trim();
      const qty = Number(count) || 0;
      const total = Number(amount) || 0;
      if (!description || (!qty && !total)) return;

      const key = description.toLowerCase();
      const current = map.get(key) || {
        description,
        count: 0,
        amountValue: 0,
      };
      current.count += qty;
      current.amountValue += total;
      map.set(key, current);
    };

    receipts.forEach((receipt) => {
      (Array.isArray(receipt.tires) ? receipt.tires : []).forEach((tire) => {
        const tireType = String(tire.condition || tire.quality || "Used")
          .trim()
          .toLowerCase();
        const label = tireType === "new" ? "New Tires" : "Used Tires";
        const quantity = Math.max(1, Number(tire.quantity) || 1);
        const amount =
          Number(tire.total_price) || (Number(tire.price) || 0) * quantity;
        addToMap(tireMap, label, quantity, amount);
      });

      const oilAmount =
        Number(receipt.oilPrice || 0) +
        Number(receipt.extraOilPrice || 0) +
        Number(receipt.oilFilterPrice || 0) +
        Number(receipt.fullServicePrice || 0);

      if (oilAmount > 0 || receipt.oil) {
        const oilName = String(receipt.oil?.name || "Oil Change").trim();
        addToMap(oilMap, oilName, 1, oilAmount);
      }

      (Array.isArray(receipt.services) ? receipt.services : []).forEach(
        (serviceItem) => {
          if (isIncludedGroupServicePart(serviceItem)) return;
          const service = serviceItem?.value ?? serviceItem ?? {};
          const name = String(
            service.name || service.service_name || "Service",
          ).trim();
          const quantity = Math.max(1, Number(service.quantity) || 1);
          const amount =
            Number(service.total_price) || (Number(service.price) || 0) * quantity;
          if (!name) return;

          if (/inspection|reinspection/i.test(name)) {
            addToMap(inspectionMap, name, quantity, amount);
            return;
          }

          addToMap(serviceMap, name, quantity, amount);
        },
      );
    });

    const toSortedRows = (map, prefix) =>
      Array.from(map.values())
        .sort(
          (a, b) =>
            b.amountValue - a.amountValue ||
            b.count - a.count ||
            a.description.localeCompare(b.description),
        )
        .map((row, index) => ({
          id: `${prefix}-${index}`,
          ...row,
        }));

    const tireRows = toSortedRows(tireMap, "tire");
    const oilRows = toSortedRows(oilMap, "oil");
    const inspectionRows = toSortedRows(inspectionMap, "inspection");
    const serviceRows = toSortedRows(serviceMap, "service");

    const sumRows = (rows) =>
      rows.reduce(
        (acc, row) => ({
          count: acc.count + (Number(row.count) || 0),
          amount: acc.amount + (Number(row.amountValue) || 0),
        }),
        { count: 0, amount: 0 },
      );

    return {
      tireRows,
      oilRows,
      inspectionRows,
      serviceRows,
      tireTotals: sumRows(tireRows),
      oilTotals: sumRows(oilRows),
      inspectionTotals: sumRows(inspectionRows),
      serviceTotals: sumRows(serviceRows),
      newTire:
        tireRows.find((row) => row.description === "New Tires") || {
          count: 0,
          amountValue: 0,
        },
      usedTire:
        tireRows.find((row) => row.description === "Used Tires") || {
          count: 0,
          amountValue: 0,
        },
    };
  }, [receipts]);

  const formatDateRange = (start, end) => {
    const startDate = parseLocalDateInput(start) || new Date(start);
    const endDate = parseLocalDateInput(end) || new Date(end);
    return `${startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })} - ${endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  };

  useEffect(() => {
    setLoading(true);
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    const params =
      startDate && endDate
        ? `?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}&tzOffsetMinutes=${tzOffsetMinutes}`
        : `?tzOffsetMinutes=${tzOffsetMinutes}`;
    axios
      .get(
        `${Helpers.baseUrl}/reports/weekly/${Helpers.authParentId}${params}`,
        Helpers.headers,
      )
      .then((response) => {
        const invoiceReceipts = (response.data.receipts || []).filter(
          isInvoiceReceipt,
        );
        setReceipts(invoiceReceipts.reverse());
        setCustomers(response.data.customers.reverse());
        setVehicle(response.data.vehicles.reverse());
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [endDate, navigate, startDate]);

  const handleStartChange = (event) => {
    const value = event.target.value;
    setStartDate(value);
    if (value > endDate) {
      setEndDate(value);
    }
  };

  const handleEndChange = (event) => {
    const value = event.target.value;
    setEndDate(value);
  };

  const breadcrumbs = [
    {
      label: "Home",
      path: "/user/dashboard",
      icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
    },
    { label: "Report", path: null },
  ];

  const statCards = [
    {
      title: "Customers",
      value: customers.length,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#667eea",
      bgColor: "rgba(102, 126, 234, 0.1)",
    },
    {
      title: "Vehicles",
      value: vehicles.length,
      icon: <DirectionsCarIcon sx={{ fontSize: 40 }} />,
      color: "#f093fb",
      bgColor: "rgba(240, 147, 251, 0.1)",
    },
    {
      title: "Receipts",
      value: receipts.length,
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: "#4facfe",
      bgColor: "rgba(79, 172, 254, 0.1)",
    },
    {
      title: "Total Sales",
      value: `$${calcPrice(receipts)}`,
      icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
      color: "#43e97b",
      bgColor: "rgba(67, 233, 123, 0.1)",
    },
  ];

  const breakdownColumns = [
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "count",
      headerName: "Count",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "amountValue",
      headerName: "Amount",
      width: 140,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }) => `$${formatCurrency(value)}`,
    },
  ];

  // const overviewCards = [
  //   {
  //     title: "New Tire Sales",
  //     count: weeklyBreakdown.newTire.count,
  //     amount: weeklyBreakdown.newTire.amountValue,
  //     color: "#2563eb",
  //     bgColor: "rgba(37, 99, 235, 0.10)",
  //   },
  //   {
  //     title: "Used Tire Sales",
  //     count: weeklyBreakdown.usedTire.count,
  //     amount: weeklyBreakdown.usedTire.amountValue,
  //     color: "#7c3aed",
  //     bgColor: "rgba(124, 58, 237, 0.10)",
  //   },
  //   {
  //     title: "Oil Changes",
  //     count: weeklyBreakdown.oilTotals.count,
  //     amount: weeklyBreakdown.oilTotals.amount,
  //     color: "#0f766e",
  //     bgColor: "rgba(15, 118, 110, 0.10)",
  //   },
  //   {
  //     title: "Inspections",
  //     count: weeklyBreakdown.inspectionTotals.count,
  //     amount: weeklyBreakdown.inspectionTotals.amount,
  //     color: "#d97706",
  //     bgColor: "rgba(217, 119, 6, 0.10)",
  //   },
  // ];

  // Customer columns
  const customerColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "name",
      headerName: "Customer Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 140,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
    },
  ];

  // Vehicle columns
  const vehicleColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "make",
      headerName: "Make",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "model",
      headerName: "Model",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "year",
      headerName: "Year",
      width: 100,
    },
    {
      field: "vin_number",
      headerName: "VIN Number",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.customer?.name || "",
    },
  ];

  // Receipt columns
  const receiptColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.customer?.name || "",
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) =>
        row.vehicle ? `${row.vehicle.make} ${row.vehicle.model}` : "",
    },
    {
      field: "totalPrice",
      headerName: "Total Price",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => `$${formatCurrency(value)}`,
    },
    {
      field: "paid",
      headerName: "Amount Paid",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => `$${formatCurrency(value)}`,
    },
    {
      field: "remaining",
      headerName: "Remaining",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => `$${formatCurrency(value)}`,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "Paid" ? "success" : "warning"}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <IconButton
          component={Link}
          to={`/user/receipts/receipt-details/${params.row._id}`}
          color="success"
          size="small"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Prepare rows
  const customerRows = customers.map((customer, index) => ({
    ...customer,
    id: customer._id,
    serial: index + 1,
  }));

  const vehicleRows = vehicles.map((vehicle, index) => ({
    ...vehicle,
    id: vehicle._id,
    serial: index + 1,
  }));

  const receiptRows = receipts.map((receipt, index) => ({
    ...receipt,
    id: receipt._id,
    serial: index + 1,
  }));

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: activeTab === 0 ? "visible" : "hidden",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          flex: activeTab === 0 ? "unset" : 1,
          display: "flex",
          flexDirection: "column",
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
          overflow: activeTab === 0 ? "visible" : "hidden",
          minHeight: activeTab === 0 ? "auto" : 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return isLast ? (
                <Typography
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {item.icon}
                  {item.label}
                </Typography>
              ) : (
                <MuiLink
                  key={index}
                  component={Link}
                  to={item.path}
                  underline="hover"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "0.875rem",
                    "&:hover": { color: "white" },
                  }}
                >
                  {item.icon}
                  {item.label}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 0.5,
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                Report
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
              >
                {formatDateRange(startDate, endDate)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <DateRangeIcon sx={{ color: "white", mr: 1 }} />
              <TextField
                type="date"
                size="small"
                label="From"
                value={startDate}
                onChange={handleStartChange}
                inputProps={{ max: endDate }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  borderRadius: 1,
                  width: 150,
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "white",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                }}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={endDate}
                onChange={handleEndChange}
                inputProps={{
                  min: startDate,
                  max: toLocalDateInput(),
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  borderRadius: 1,
                  width: 150,
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "white",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                }}
              />
            </Stack>
          </Box>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {statCards.map((stat, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
                >
                  <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {stat.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: stat.color,
                        }}
                      >
                        {loading ? "..." : stat.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {formatDateRange(startDate, endDate)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: stat.bgColor,
                        color: stat.color,
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Stack>
                  </CardContent>
                </Card>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            flex: activeTab === 0 ? "unset" : 1,
            display: "flex",
            flexDirection: "column",
            overflow: activeTab === 0 ? "visible" : "hidden",
            minHeight: activeTab === 0 ? "auto" : 0,
            mb: 5.25,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  minHeight: "56px",
                },
              }}
            >
              <Tab
                icon={<AssessmentIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Overview"
              />
              <Tab
                icon={<ReceiptIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`Receipts (${receipts.length})`}
              />
              <Tab
                icon={<PeopleIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`Customers (${customers.length})`}
              />
              <Tab
                icon={<DirectionsCarIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`Vehicles (${vehicles.length})`}
              />
            </Tabs>
          </Box>

          <Box
            sx={{
              flex: activeTab === 0 ? "unset" : 1,
              overflow: activeTab === 0 ? "visible" : "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: activeTab === 0 ? "auto" : 0,
            }}
          >
            {activeTab === 0 && (
              <Box
                sx={{
                  overflow: "visible",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {/* <Grid container spacing={3}>
                  {overviewCards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.title}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.75, fontWeight: 600 }}
                              >
                                {card.title}
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: card.color }}
                              >
                                {loading ? "..." : card.count}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mt: 0.5, color: card.color, fontWeight: 600 }}
                              >
                                {loading ? "..." : `$${formatCurrency(card.amount)}`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2,
                                backgroundColor: card.bgColor,
                              }}
                            />
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid> */}

                <Grid container spacing={3} alignItems="stretch">
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                    >
                      {renderSectionTitle(
                        "Tires",
                        <TireRepair sx={{ fontSize: 20 }} />,
                        "#2563eb",
                      )}
                      <ReusableDataGrid
                        rows={weeklyBreakdown.tireRows}
                        loading={loading}
                        columns={breakdownColumns}
                        pageSize={10}
                        disableSorting
                        disablePagination
                        disableInternalScroll
                        noRowsMessage="No tire sales in this date range"
                      />
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                    >
                      {renderSectionTitle(
                        "Inspections",
                        <FactCheckIcon sx={{ fontSize: 20 }} />,
                        "#d97706",
                      )}
                      <ReusableDataGrid
                        rows={weeklyBreakdown.inspectionRows}
                        loading={loading}
                        columns={breakdownColumns}
                        pageSize={10}
                        disableSorting
                        disablePagination
                        disableInternalScroll
                        noRowsMessage="No inspection services in this date range"
                      />
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                    >
                      {renderSectionTitle(
                        "Oil Changes",
                        <OilBarrel sx={{ fontSize: 20 }} />,
                        "#0f766e",
                      )}
                      <ReusableDataGrid
                        rows={weeklyBreakdown.oilRows}
                        loading={loading}
                        columns={breakdownColumns}
                        pageSize={10}
                        disableSorting
                        disablePagination
                        disableInternalScroll
                        noRowsMessage="No oil changes in this date range"
                      />
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                    >
                      {renderSectionTitle(
                        "Services",
                        <CarRepair sx={{ fontSize: 20 }} />,
                        "#7c3aed",
                      )}
                      <ReusableDataGrid
                        rows={weeklyBreakdown.serviceRows}
                        loading={loading}
                        columns={breakdownColumns}
                        pageSize={10}
                        disableSorting
                        disablePagination
                        disableInternalScroll
                        noRowsMessage="No additional service lines in this date range"
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 1 && (
              <ReusableDataGrid
                rows={receiptRows}
                loading={loading}
                columns={receiptColumns}
                pageSize={10}
                pageSizeOptions={[10, 25, 50, 100]}
                noRowsMessage="No receipts available for this date range"
                autoHeight={false}
                sx={{
                  flex: 1,
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.04)",
                  },
                }}
              />
            )}

            {activeTab === 2 && (
              <ReusableDataGrid
                rows={customerRows}
                loading={loading}
                columns={customerColumns}
                pageSize={10}
                pageSizeOptions={[10, 25, 50, 100]}
                noRowsMessage="No new customers for this date range"
                autoHeight={false}
                sx={{
                  flex: 1,
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.04)",
                  },
                }}
              />
            )}

            {activeTab === 3 && (
              <ReusableDataGrid
                rows={vehicleRows}
                loading={loading}
                columns={vehicleColumns}
                pageSize={10}
                pageSizeOptions={[10, 25, 50, 100]}
                noRowsMessage="No new vehicles for this date range"
                autoHeight={false}
                sx={{
                  flex: 1,
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.04)",
                  },
                }}
              />
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default WeeklyReports;
